/* eslint-disable @typescript-eslint/no-explicit-any */
import { captureException } from "@sentry/react";
import { useEffect, useMemo } from "react";
import { BehaviorSubject, distinctUntilChanged, Observable, tap } from "rxjs";
import { useSyncExternalStore } from "use-sync-external-store/shim";

/** `undefined` is excluded: it cannot be told apart from an omitted key, so pass one as `x ?? null`. */
type SubscriptionKey = string | number | boolean | null;

interface useObservableType {
  <T, InitT = T | undefined>(obs: Observable<T> | null, initial?: InitT): T | InitT;
  <T, InitT = T | undefined>(
    obs: Observable<T> | null,
    initial: InitT,
    subscriptionKey: SubscriptionKey,
  ): T | InitT;
}

export const useObservable: useObservableType = (obs$, initial, subscriptionKey?: SubscriptionKey) => {
  // `initial` seeds the subject once, by design.
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  const data$ = useMemo(() => new BehaviorSubject<any>(initial), []);
  useEffect(() => () => data$.complete(), [data$]);
  const subscriptionIdentity = subscriptionKey === undefined ? obs$ : subscriptionKey;
  const sub = useMemo(
    () => (onStorageChange: () => void) => {
      const subscription = obs$
        ?.pipe(
          distinctUntilChanged(),
          tap((r) => data$.next(r)),
        )
        ?.subscribe({
          next: onStorageChange,
          error: (e) => {
            captureException(e);
          },
        });
      return () => subscription?.unsubscribe();
    },
    // Resubscription is keyed on `subscriptionIdentity`, never on the closed-over `obs$`.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [subscriptionIdentity, data$],
  );
  const getCur = useMemo(() => () => data$?.getValue() ?? null, [data$]);
  return useSyncExternalStore(sub, getCur, getCur);
};
